import apiClient from "@toothfairy/shared-api/apiClient";
import secureRequest from "@toothfairy/shared-api/secureAPI";
import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import envConfig from "../../envConfig";

/**
 * Trigger catalog client — the frontend's ONLY interface to the trigger
 * catalog. The single source of truth lives on the backend
 * (backend-core-1/integrations/triggers/catalog.js) and is served by the
 * triggerCatalogApi REST endpoint: GET {REST_ENDPOINT}/triggers/catalog.
 *
 * This module must NOT keep a hardcoded copy of providers/events. It fetches,
 * caches (in-memory + localStorage) and exposes lookup helpers that the Triggers
 * UI consumes. Components should call `loadTriggerCatalog()` on mount and read
 * the result via Redux (`state.triggerCatalog`) or the in-memory cache here.
 */

const CATALOG_URL = `${envConfig.REACT_APP_REST_ENDPOINT}/triggers/catalog`;
const CACHE_KEY = "tf_trigger_catalog_v1";
const CACHE_TTL_MS = 1000 * 60 * 60; // 1 hour

let _catalog = {
  version: null,
  nativeEvents: [],
  providers: [],
};

const _readCached = () => {
  try {
    const raw = localStorage?.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt) return null;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return parsed.catalog;
  } catch {
    return null;
  }
};

const _writeCache = (catalog) => {
  try {
    localStorage?.setItem(
      CACHE_KEY,
      JSON.stringify({ fetchedAt: Date.now(), catalog })
    );
  } catch {
    /* ignore quota / SSR errors */
  }
};

const _applyCatalog = (catalog) => {
  _catalog = {
    version: catalog?.version || null,
    nativeEvents: Array.isArray(catalog?.nativeEvents)
      ? catalog.nativeEvents
      : [],
    providers: Array.isArray(catalog?.providers) ? catalog.providers : [],
  };
};

// Hydrate from localStorage cache on module load so the UI is not empty on the
// very first paint before the fetch resolves.
const _bootstrapped = _readCached();
if (_bootstrapped) _applyCatalog(_bootstrapped);

const getCatalog = () => _catalog;

const getNativeEvents = () => _catalog.nativeEvents;
const getProviders = () => _catalog.providers;

const findNativeEvent = (id) => _catalog.nativeEvents.find((e) => e.id === id);

const findProvider = (id) => _catalog.providers.find((p) => p.id === id);

const findProviderEvent = (providerId, eventId) => {
  const provider = findProvider(providerId);
  if (!provider) return null;
  return provider.events.find((e) => e.id === eventId) || null;
};

/**
 * Fetch the catalog from the backend (Cognito-secured) and update the in-memory
 * + localStorage caches. Returns the resolved catalog.
 *
 * @param {boolean} force — bypass the in-memory freshness check.
 */
const loadTriggerCatalog = async (force = false) => {
  if (!force && _catalog.providers.length > 0) return _catalog;

  const payload = await AppAuth.getCurrentSession();
  const res = await secureRequest(
    apiClient.apiClient,
    "GET",
    `${CATALOG_URL}?version=${_catalog.version || ""}`,
    null,
    null,
    true,
    null,
    payload
  );

  const body = res?.data;
  // axios-style response or raw fetch body
  const data =
    body && typeof body === "object" && "nativeEvents" in body
      ? body
      : (() => {
          try {
            return JSON.parse(body);
          } catch {
            return null;
          }
        })();

  if (data && Array.isArray(data.providers)) {
    _applyCatalog(data);
    _writeCache(data);
  }
  return _catalog;
};

export {
  loadTriggerCatalog,
  getCatalog,
  getNativeEvents,
  getProviders,
  findNativeEvent,
  findProvider,
  findProviderEvent,
};

export default {
  loadTriggerCatalog,
  getCatalog,
  getNativeEvents,
  getProviders,
  findNativeEvent,
  findProvider,
  findProviderEvent,
};
